<template>
  <v-app>
    <v-container fluid>
      <v-row v-if="$insertAccess($route.fullPath)">
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                <v-icon class="me-2">mdi-alert-box</v-icon>
                Inventory Summary - Stock Alert Report
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form @submit.prevent="loadReport" ref="filterForm">
                  <v-row class="py-3 px-3" align="end">
                    <v-col cols="12" sm="6" md="3">
                      <v-autocomplete
                        v-model="filters.warehouse_id"
                        :items="warehouseList"
                        :item-title="(item) => item.name || ''"
                        item-value="id"
                        label="Warehouse (Optional)"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        clearable
                        hide-details="auto"
                        :loading="warehousesLoading"
                        placeholder="All Warehouses"
                      
                          :menu-props="{ contentClass: 'my-custom-dropdown' }"
/>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <v-autocomplete
                        v-model="filters.product_id"
                        :items="getProductMapList"
                        :item-title="(item) => (item?.product?.code ? `${item?.product?.code} - ` : '') + (item?.product?.name || '')"
                        item-value="id"
                        label="Product (Optional)"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        clearable
                        hide-details="auto"
                        :loading="productsLoading"
                        placeholder="All Products"
                      
                          :menu-props="{ contentClass: 'my-custom-dropdown' }"
/>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.status"
                        :items="statusOptions"
                        item-title="text"
                        item-value="value"
                        label="Stock Status"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        hide-details="auto"
                        clearable
                      
                          :menu-props="{ contentClass: 'my-custom-dropdown' }"
/>
                    </v-col>
                    <v-col cols="12" sm="6" md="2" class="d-flex align-end">
                      <v-btn
                        type="submit"
                        color="primary"
                        variant="flat"
                        :loading="loading"
                        prepend-icon="mdi-magnify"
                        density="compact"
                        height="40"
                      >
                        Search
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- Report Table -->
          <v-expansion-panels v-model="reportPanel" class="mt-2">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
                expand-icon="mdi-chevron-down"
              >
                <v-icon class="me-2">mdi-table</v-icon>
                Report Data
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-row class="py-2">
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="search"
                      append-inner-icon="mdi-magnify"
                      label="Search"
                      single-line
                      hide-details
                      density="compact"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      class="pl-2"
                      clearable
                    />
                  </v-col>
                  <v-col md="5" cols="12">
                    <div class="d-flex align-center flex-column">
                      <v-btn-group
                        variant="outlined"
                        density="compact"
                        size="x-small"
                        divided
                      >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="success"
                          @click="exportToCSV"
                          >CSV</v-btn
                        >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="primary"
                          @click="exportToExcel"
                          >EXCEL</v-btn
                        >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="red"
                          @click="exportToPDF"
                          >PDF</v-btn
                        >
                      </v-btn-group>
                    </div>
                  </v-col>
                  <v-col md="3" cols="12" />
                </v-row>
                <v-alert
                  v-if="!loading && filteredReportData.length === 0"
                  type="info"
                  variant="tonal"
                >
                  No stock alert data found for the selected criteria.
                </v-alert>

                <div v-if="filteredReportData.length" class="table-container">

                  <v-data-table-virtual
                    :headers="headers"
                    :items="filteredReportData"
                    :loading="loading"
                    v-model:items-per-page="itemsPerPage"
                    :search="search"
                    density="compact"
                    height="400"
                    item-value="name"
                    fixed-header
                    class="table-bordered text-caption"
                  >
                    <template v-slot:item.index="{ index }">
                      {{ ++index }}
                    </template>

                    <template v-slot:item.product_code="{ item }">
                      {{ productCode(item) }}
                    </template>

                    <template v-slot:item.product_name="{ item }">
                      {{ productName(item) }}
                    </template>

                    <template v-slot:item.warehouse_name="{ item }">
                      {{ warehouseName(item) }}
                    </template>

                    <template v-slot:item.stock_alert_quantity="{ item }">
                      <div style="text-align: center;">{{ formatNumber(item.stock_alert_quantity) }}</div>
                    </template>

                    <template v-slot:item.current_quantity="{ item }">
                      <div style="text-align: center;">{{ formatNumber(item.current_quantity) }}</div>
                    </template>

                    <template v-slot:item.stock_status="{ item }">
                      <div style="text-align: center;">
                        <v-chip :color="stockStatusColor(item.stock_status)" size="small" variant="flat">
                          {{ item.stock_status || '-' }}
                        </v-chip>
                      </div>
                    </template>

                    <template v-slot:item.last_purchase_price="{ item }">
                      <div style="text-align: right;">{{ formatNumber(item.last_purchase_price, 2) }}</div>
                    </template>

                    <template v-slot:item.last_sale_price="{ item }">
                      <div style="text-align: right;">{{ formatNumber(item.last_sale_price, 2) }}</div>
                    </template>

                    <template v-slot:item.stock_valuation="{ item }">
                      <div style="text-align: right;">{{ formatNumber(item.stock_valuation, 2) }}</div>
                    </template>
                  </v-data-table-virtual>

                  <!-- sticky footer inside scroll area -->
                  <div class="table-footer">
                    <div class="d-flex justify-end align-center px-3 py-2" style="font-weight:700">
                      <div style="min-width:320px; text-align:right;">Total:</div>
                      <div style="width:120px; text-align:center;">{{ formatNumber(totals.current_quantity, 2) }}</div>
                      <div style="width:120px; text-align:center;">-</div>
                      <div style="width:120px; text-align:right;">-</div>
                      <div style="width:120px; text-align:right;">-</div>
                      <div style="width:160px; text-align:right;">{{ formatNumber(totals.stock_valuation, 2) }}</div>
                    </div>
                  </div>

                  <!-- legend / stock status styles under the table as requested -->
                  <v-row class="mt-2">
                    <v-col cols="12">
                      <div class="d-flex gap-3 align-center ma-3">
                        <v-chip color="error" size="small" variant="flat">Out of Stock</v-chip>
                        <v-chip color="warning" size="small" variant="flat">Insufficient</v-chip>
                        <v-chip color="success" size="small" variant="flat">Sufficient</v-chip>
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import { ref, reactive, computed, onMounted, getCurrentInstance } from 'vue'
import { toast } from 'vue3-toastify'
import { useStore } from 'vuex'
import Axios from 'axios'
import { getInventoryAlertStockSummary } from '@/store/api'

export default {
  data: () => ({
    itemsPerPage: 10,
    panel: [0],
    reportPanel: [0],
    search: '',
    headers: [
      { title: '#SL', align: 'center', key: 'index', width: '80px', sortable: false },
      { title: 'Product Code', key: 'product_code', align: 'left', sortable: false, width: '150px' },
      { title: 'Product Name', key: 'product_name', align: 'left', sortable: false, width: '300px' },
      { title: 'Warehouse', key: 'warehouse_name', align: 'left', sortable: false },
      { title: 'Alert Qty', key: 'stock_alert_quantity', align: 'center', sortable: false, width: '120px' },
      { title: 'Current Qty', key: 'current_quantity', align: 'center', sortable: false, width: '120px' },
      { title: 'Stock Status', key: 'stock_status', align: 'center', sortable: false, width: '140px' },
      { title: 'Last Purchase Price', key: 'last_purchase_price', align: 'right', sortable: false, width: '160px' },
      { title: 'Last Sale Price', key: 'last_sale_price', align: 'right', sortable: false, width: '160px' },
      { title: 'Stock Valuation', key: 'stock_valuation', align: 'right', sortable: false, width: '160px' }
    ]
  }),
  setup() {
    const store = useStore()
    const instance = getCurrentInstance()
    const appContext = instance?.appContext

    const loading = ref(false)
    const warehousesLoading = ref(false)
    const productsLoading = ref(false)

    const filters = reactive({ warehouse_id: null, product_id: null, status: '' })

    const statusOptions = [
      { text: 'All', value: '' },
      { text: 'Out of Stock', value: 'stock_out' },
      { text: 'Insufficient', value: 'insufficient' },
      { text: 'Sufficient', value: 'sufficient' }
    ]

    const warehouseList = computed(() => store.getters.getWarehouseList || [])
    const getProductMapList = computed(() => (store.getters.getProductMapList || []).filter((item) => item.status == 1))

    const reportData = ref([])

    const formatNumber = (value, fraction = 0) => {
      if (value === undefined || value === null || isNaN(value)) return '-'
      return Number(value).toLocaleString(undefined, { minimumFractionDigits: fraction, maximumFractionDigits: fraction })
    }

    const productCode = (row) => row.product?.code ?? row.product_code ?? '-'
    const productName = (row) => row.product?.name ?? row.product_name ?? '-'
    const warehouseName = (row) => row.warehouse?.name ?? row.warehouse_name ?? '-'

    const stockStatusColor = (status) => {
      if (!status) return 'grey'
      const s = String(status).toLowerCase()
      if (s === 'out of stock') return 'error'
      if (s === 'insufficient') return 'warning'
      if (s === 'sufficient') return 'success'
      return 'grey'
    }

    const filteredReportData = computed(() => {
      if (!ref('').value) {} // preserve reactivity
      // `search` is returned from data(), will be reactive on component instance
      return reportData.value.filter((row) => {
        const searchVal = (instance?.proxy?.search || '').toLowerCase()
        if (!searchVal) return true
        const code = productCode(row).toLowerCase()
        const name = productName(row).toLowerCase()
        const warehouse = warehouseName(row).toLowerCase()
        return code.includes(searchVal) || name.includes(searchVal) || warehouse.includes(searchVal)
      })
    })

    const totals = computed(() => {
      return filteredReportData.value.reduce(
        (acc, row) => {
          acc.current_quantity += Number(row.current_quantity) || 0
          acc.stock_valuation += Number(row.stock_valuation) || 0
          return acc
        },
        { current_quantity: 0, stock_valuation: 0 }
      )
    })

    const loadWarehouses = async () => {
      if (store.getters.getWarehouseList?.length > 0) return
      warehousesLoading.value = true
      try {
        await store.dispatch('getWarehouseList', {})
      } catch (e) {
        toast.error(e?.message || 'Failed to load warehouses')
      } finally {
        warehousesLoading.value = false
      }
    }

    const loadProducts = async () => {
      if (store.getters.getProductMapList?.length > 0) return
      productsLoading.value = true
      try {
        await store.dispatch('getProductMapList', {})
      } catch (e) {
        toast.error(e?.message || 'Failed to load products')
      } finally {
        productsLoading.value = false
      }
    }

    const loadReport = async () => {
      loading.value = true
      try {
        const params = {}
        if (filters.warehouse_id != null && filters.warehouse_id !== '') params.warehouse_id = filters.warehouse_id
        if (filters.product_id != null && filters.product_id !== '') params.product_id = filters.product_id
        if (filters.status != null && filters.status !== '') params.status = filters.status

        const response = await Axios.get(getInventoryAlertStockSummary, { params })

        if (response.data?.success) {
          const data = response.data.data
          reportData.value = data?.product_stock ?? []
          if (reportData.value.length === 0) {
            toast.info('No data found for the selected criteria')
          }
        } else {
          toast.error(response.data?.message || 'Failed to load report')
          reportData.value = []
        }
      } catch (error) {
        toast.error(error.response?.data?.message || error.message || 'Failed to load report')
        reportData.value = []
      } finally {
        loading.value = false
      }
    }

    const buildExportData = () => {
      const data = [
        ['Inventory Summary - Stock Alert Report'],
        [],
        ['# SL', 'Product Code', 'Product Name', 'Warehouse', 'Alert Qty', 'Current Qty', 'Stock Status', 'Last Purchase Price', 'Last Sale Price', 'Stock Valuation'],
        []
      ]
      reportData.value.forEach((row, index) => {
        data.push([
          index + 1,
          productCode(row),
          productName(row),
          warehouseName(row),
          formatNumber(row.stock_alert_quantity),
          formatNumber(row.current_quantity),
          row.stock_status || '-',
          formatNumber(row.last_purchase_price, 2),
          formatNumber(row.last_sale_price, 2),
          formatNumber(row.stock_valuation, 2)
        ])
      })
      if (reportData.value.length > 0) {
        data.push([])
        data.push(['', '', '', '', 'Total:', formatNumber(totals.value.current_quantity), '-', '-', '-', formatNumber(totals.value.stock_valuation, 2)])
      }
      return data
    }

    const exportToCSV = () => {
      if (!reportData.value.length) {
        toast.warning('No data to export')
        return
      }
      const proxy = appContext?.config?.globalProperties
      if (proxy?.$exportTableToCSV && proxy?.$fileDate) {
        proxy.$exportTableToCSV(buildExportData(), 'inventory_stock_alert_report_' + proxy.$fileDate())
      }
    }

    const exportToExcel = () => {
      if (!reportData.value.length) {
        toast.warning('No data to export')
        return
      }
      const proxy = appContext?.config?.globalProperties
      if (proxy?.$exportTableToExcel && proxy?.$fileDate) {
        proxy.$exportTableToExcel(buildExportData(), 'inventory_stock_alert_report_' + proxy.$fileDate())
      }
    }

    const exportToPDF = () => {
      if (!reportData.value.length) {
        toast.warning('No data to export')
        return
      }

      const headers = ['# SL', 'Product Code', 'Product Name', 'Warehouse', 'Alert Qty', 'Current Qty', 'Stock Status', 'Last Purchase Price', 'Last Sale Price', 'Stock Valuation']
      const rows = reportData.value.map((row, index) => [
        index + 1,
        productCode(row),
        productName(row),
        warehouseName(row),
        formatNumber(row.stock_alert_quantity),
        formatNumber(row.current_quantity),
        row.stock_status || '-',
        formatNumber(row.last_purchase_price, 2),
        formatNumber(row.last_sale_price, 2),
        formatNumber(row.stock_valuation, 2)
      ])

      rows.push(['', '', '', '', 'Total:', formatNumber(totals.value.current_quantity), '-', '-', '-', formatNumber(totals.value.stock_valuation, 2)])

      const proxy = appContext?.config?.globalProperties
      if (proxy?.$exportTableToPDF && proxy?.$fileDate) {
        proxy.$exportTableToPDF(
          headers,
          rows,
          'Inventory Stock Alert Report',
          'inventory_stock_alert_report_' + proxy.$fileDate()
        )
      }
    }

    onMounted(() => {
      loadWarehouses()
      loadProducts()
    })

    return {
      store,
      loading,
      warehousesLoading,
      productsLoading,
      filters,
      statusOptions,
      warehouseList,
      getProductMapList,
      reportData,
      formatNumber,
      productCode,
      productName,
      warehouseName,
      stockStatusColor,
      filteredReportData,
      totals,
      loadReport,
      exportToCSV,
      exportToExcel,
      exportToPDF
    }
  },
  methods: {},
  mounted() {
    // nothing additional
  }
}
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}

.table-bordered :deep(th),
.table-bordered :deep(td) {
  border: 1px solid #000000 !important;
}

.table-bordered :deep(table) {
  border-collapse: collapse;
}

.table-container {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.table-scroll {
  max-height: 48vh;
  overflow: auto;
  position: relative;
}

.table-scroll :deep thead th {
  position: sticky;
  top: 0;
  background: #ffffff;
  z-index: 2;
}

.table-footer {
  position: sticky;
  bottom: 0;
  background: #ffffff;
  z-index: 2;
  border-top: 1px solid #e0e0e0;
}
</style>